import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';

import { bookingStatusTone } from '@/lib/booking-status';

import type { DatesSetArg, EventClickArg } from '@fullcalendar/core';
import type { BookingWithRelations } from '@repo/shared-types';

interface BookingCalendarProps {
  bookings: BookingWithRelations[];
  initialDate?: string;
  onEventClick: (bookingId: string) => void;
  onDatesChange: (start: string, end: string) => void;
}

export const BookingCalendar = ({
  bookings,
  initialDate,
  onEventClick,
  onDatesChange,
}: BookingCalendarProps) => {
  const events = bookings.map((booking) => {
    const tone = bookingStatusTone(booking.status);
    const attendance = (booking as BookingWithRelations & { attendance?: string | null })
      .attendance;
    const hasTime = (booking as BookingWithRelations & { has_time?: boolean }).has_time !== false;
    const start = new Date(booking.date);
    if (!hasTime) {
      start.setHours(0, 0, 0, 0);
    }
    const end = new Date(start.getTime() + 60 * 60 * 1000);

    // Status wins over attendance: a cancelled or rescheduled visit should not
    // read as "attended" just because it was marked so before the change.
    const colors =
      booking.status !== 'active'
        ? tone.calendar
        : attendance === 'not_attended'
          ? { bg: 'rgba(239, 68, 68, 0.1)', border: 'rgb(239, 68, 68)', text: 'rgb(185, 28, 28)' }
          : attendance === 'attended'
            ? { bg: 'rgba(34, 197, 94, 0.1)', border: 'rgb(34, 197, 94)', text: 'rgb(21, 128, 61)' }
            : tone.calendar;

    return {
      id: booking.id,
      title: booking.title,
      start,
      end,
      allDay: false,
      backgroundColor: colors.bg,
      borderColor: colors.border,
      textColor: colors.text,
      classNames: tone.struck ? ['line-through', 'opacity-50'] : [],
      extendedProps: { status: booking.status, attendance },
    };
  });

  const handleEventClick = (info: EventClickArg) => {
    onEventClick(info.event.id);
  };

  const handleDatesSet = (dateInfo: DatesSetArg) => {
    onDatesChange(dateInfo.startStr, dateInfo.endStr);
  };

  return (
    <div className="booking-calendar flex-1 overflow-auto px-6 pb-6">
      <style>{`
        .booking-calendar .fc {
          --fc-border-color: hsl(var(--border) / 0.4);
          --fc-button-bg-color: hsl(var(--secondary));
          --fc-button-border-color: hsl(var(--border));
          --fc-button-text-color: hsl(var(--foreground));
          --fc-button-hover-bg-color: hsl(var(--accent));
          --fc-button-hover-border-color: hsl(var(--border));
          --fc-button-active-bg-color: hsl(var(--primary));
          --fc-button-active-border-color: hsl(var(--primary));
          --fc-button-active-text-color: hsl(var(--primary-foreground));
          --fc-today-bg-color: hsl(var(--primary) / 0.04);
          --fc-page-bg-color: transparent;
          --fc-neutral-bg-color: hsl(var(--muted) / 0.3);
          --fc-event-border-color: transparent;
          font-family: inherit;
        }
        .booking-calendar .fc .fc-toolbar-title {
          font-size: 1.1rem;
          font-weight: 600;
        }
        .booking-calendar .fc .fc-button {
          font-size: 0.8rem;
          padding: 0.3rem 0.7rem;
          border-radius: 6px;
          font-weight: 500;
          box-shadow: none !important;
          text-transform: capitalize;
        }
        .booking-calendar .fc .fc-button-group > .fc-button {
          border-radius: 0;
        }
        .booking-calendar .fc .fc-button-group > .fc-button:first-child {
          border-radius: 6px 0 0 6px;
        }
        .booking-calendar .fc .fc-button-group > .fc-button:last-child {
          border-radius: 0 6px 6px 0;
        }
        .booking-calendar .fc .fc-col-header-cell {
          padding: 0.5rem 0;
          font-weight: 500;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: hsl(var(--muted-foreground));
        }
        .booking-calendar .fc .fc-daygrid-day-number {
          font-size: 0.8rem;
          padding: 0.4rem 0.5rem;
          color: hsl(var(--foreground) / 0.7);
        }
        .booking-calendar .fc .fc-daygrid-day.fc-day-today .fc-daygrid-day-number {
          background: hsl(var(--primary));
          color: hsl(var(--primary-foreground));
          border-radius: 50%;
          width: 1.6rem;
          height: 1.6rem;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0.25rem;
        }
        .booking-calendar .fc .fc-event {
          border-radius: 4px;
          padding: 1px 6px;
          font-size: 0.75rem;
          font-weight: 500;
          cursor: pointer;
          border-left-width: 3px;
          margin-bottom: 1px;
        }
        .booking-calendar .fc .fc-list-table {
          border-color: hsl(var(--border) / 0.4);
        }
        .booking-calendar .fc .fc-list-event td {
          padding: 0.5rem 0.75rem;
          font-size: 0.8rem;
          background: transparent;
          border-color: hsl(var(--border) / 0.2);
        }
        .booking-calendar .fc .fc-list-event:hover td {
          background: hsl(var(--accent) / 0.5) !important;
        }
        .booking-calendar .fc .fc-list-day-cushion {
          background: hsl(var(--muted) / 0.3) !important;
          font-size: 0.8rem;
          font-weight: 600;
        }
        .booking-calendar .fc .fc-list-empty {
          background: transparent;
        }
        .booking-calendar .fc .fc-scrollgrid {
          border-radius: 8px;
          overflow: hidden;
        }
        .booking-calendar .fc .fc-daygrid-day-frame {
          min-height: 5.5rem;
        }
      `}</style>
      <FullCalendar
        plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        initialDate={initialDate}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,listWeek,listDay',
        }}
        buttonText={{
          month: 'Month',
          listWeek: 'Week',
          listDay: 'Day',
          today: 'Today',
        }}
        events={events}
        eventClick={handleEventClick}
        datesSet={handleDatesSet}
        height="auto"
        eventDisplay="block"
        dayMaxEvents={3}
        nowIndicator
        eventTimeFormat={{
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }}
      />
    </div>
  );
};
